"use client";
import React, { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Brain, Sparkles } from "lucide-react";
import { useAuth } from "@/app/hooks";
import { api_url } from "@/app/api";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import {
   Button,
   Switch,
   Loading,
   Dialog,
   DialogContent,
   DialogHeader,
   DialogTitle,
   DialogDescription,
   DialogFooter
} from "@/app/components/ui";
import { cn } from "@/app/lib/utils";
import { METHOD_TYPE } from "@/app/lib/enums";

interface UserSettingsDialogProps {
   open: boolean;
   onOpenChange: (open: boolean) => void;
   isFirstTime?: boolean;
}

interface ProjectSettings {
   projectName: string;
   enabled: boolean;
   allowAnalytics: boolean;
}

export function UserSettingsDialog({
   open,
   onOpenChange,
   isFirstTime = false
}: UserSettingsDialogProps) {
   const translate = useTranslations("UserSettings");
   const { checkAuth } = useAuth();
   const [settings, setSettings] = useState<ProjectSettings[]>([]);
   const [isLoading, setIsLoading] = useState(false);
   const [isSaving, setIsSaving] = useState(false);
   const [message, setMessage] = useState<{
      type: "success" | "error";
      text: string;
   } | null>(null);

   useEffect(() => {
      if (open) {
         fetchSettings();
      } else {
         setMessage(null);
      }
   }, [open]);

   const fetchSettings = async () => {
      try {
         setIsLoading(true);
         const res = await fetchWithAuth(api_url.user_memory_my_settings_url);
         const json = await res.json();
         if (res.ok && json.data) {
            setSettings(json.data);
         }
      } catch (err) {
         console.error("Error loading user settings:", err);
      } finally {
         setIsLoading(false);
      }
   };

   const handleToggleMemory = (projectName: string, val: boolean) => {
      setSettings((prev) =>
         prev.map((s) => (s.projectName === projectName ? { ...s, enabled: val } : s))
      );
   };

   const handleToggleAnalytics = (projectName: string, val: boolean) => {
      setSettings((prev) =>
         prev.map((s) =>
            s.projectName === projectName ? { ...s, allowAnalytics: val } : s
         )
      );
   };

   const handleSave = async () => {
      try {
         setIsSaving(true);
         setMessage(null);
         const res = await fetchWithAuth(api_url.user_memory_my_settings_url, {
            method: METHOD_TYPE.POST,
            headers: {
               "Content-Type": "application/json"
            },
            body: JSON.stringify({ settings })
         });
         const json = await res.json();
         if (res.ok && json.success) {
            setMessage({ type: "success", text: translate("successMessage") });
            await checkAuth({ forceRefresh: true });
            setTimeout(() => {
               onOpenChange(false);
            }, 1000);
         } else {
            setMessage({ type: "error", text: json.error || translate("errorMessage") });
         }
      } catch (err: any) {
         console.error("Error saving user settings:", err);
         setMessage({ type: "error", text: err?.message || translate("errorMessage") });
      } finally {
         setIsSaving(false);
      }
   };

   return (
      <Dialog open={open} onOpenChange={onOpenChange}>
         <DialogContent
            preventOutsideClose={false}
            hideCloseButton={false}
            className="w-full h-full sm:h-auto max-h-none border-none sm:border rounded-none sm:rounded-3xl p-0 overflow-hidden sm:max-w-[800px] sm:max-h-none left-0 top-0 translate-x-0 translate-y-0 sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] dark:bg-[#151518] dark:border-[#2d2d34]"
         >
            <div className="overflow-y-auto sm:overflow-y-visible h-full sm:h-auto w-full sm:max-h-none p-6 sm:p-10 space-y-3 sm:space-y-3">
               <DialogHeader className="pb-2.5">
                  <DialogTitle className="text-xl font-bold flex items-center gap-2">
                     <span>{translate("title")}</span>
                  </DialogTitle>
                  <DialogDescription className="text-sm pt-1.5 leading-relaxed text-muted-foreground">
                     {translate("introduction")}
                  </DialogDescription>
               </DialogHeader>

               {isLoading ? (
                  <div className="flex justify-center items-center py-8">
                     <Loading size="md" />
                  </div>
               ) : (
                  <div className="space-y-2.5 my-0">
                     {settings.length > 0 && (
                        <>
                           {/* Part 1: Personalization (Memory) */}
                           <div className="space-y-2.5 p-3 sm:p-4 rounded-xl border bg-transparent dark:bg-transparent text-card-foreground dark:border-[#2d2d34]">
                              <div className="flex gap-3 pb-2.5 border-b border-dashed dark:border-[#2d2d34]/60">
                                 <Brain className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5" />
                                 <div>
                                    <span className="font-bold text-sm sm:text-base text-foreground block mb-0.5">
                                       {translate("personalizationLabel")}
                                    </span>
                                    <span className="text-xs sm:text-sm text-muted-foreground leading-normal block">
                                       {translate("personalizationDesc")}
                                    </span>
                                    <span className="text-[11px] sm:text-xs text-muted-foreground/70 leading-normal block mt-1 italic">
                                       ⏱ {translate("memorySyncNote")}
                                    </span>
                                 </div>
                              </div>
                              <div className="space-y-1 pt-0.5">
                                 {settings.map((item) => (
                                    <div
                                       key={`${item.projectName}-memory`}
                                       className="flex items-center justify-between py-1.5 sm:py-1.5 px-1.5 sm:px-2.5 hover:bg-muted/10 dark:hover:bg-white/5 rounded-lg transition-colors"
                                    >
                                       <span className="text-xs sm:text-sm font-semibold text-foreground uppercase tracking-wide">
                                          {item.projectName}
                                       </span>
                                       <Switch
                                          checked={item.enabled}
                                          onCheckedChange={(val) =>
                                             handleToggleMemory(item.projectName, val)
                                          }
                                       />
                                    </div>
                                 ))}
                              </div>
                           </div>

                           {/* Part 2: Improve system (Analytics) */}
                           <div className="space-y-2.5 p-3 sm:p-4 rounded-xl border bg-transparent dark:bg-transparent text-card-foreground dark:border-[#2d2d34]">
                              <div className="flex gap-3 pb-2.5 border-b border-dashed dark:border-[#2d2d34]/60">
                                 <Sparkles className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                                 <div>
                                    <span className="font-bold text-sm sm:text-base text-foreground block mb-0.5">
                                       {translate("allowAnalyticsLabel")}
                                    </span>
                                    <span className="text-xs sm:text-sm text-muted-foreground leading-normal block">
                                       {translate("allowAnalyticsDesc")}
                                    </span>
                                 </div>
                              </div>
                              <div className="space-y-1 pt-0.5">
                                 {settings.map((item) => (
                                    <div
                                       key={`${item.projectName}-analytics`}
                                       className="flex items-center justify-between py-1.5 sm:py-1.5 px-1.5 sm:px-2.5 hover:bg-muted/10 dark:hover:bg-white/5 rounded-lg transition-colors"
                                    >
                                       <span className="text-xs sm:text-sm font-semibold text-foreground uppercase tracking-wide">
                                          {item.projectName}
                                       </span>
                                       <Switch
                                          checked={item.allowAnalytics}
                                          onCheckedChange={(val) =>
                                             handleToggleAnalytics(item.projectName, val)
                                          }
                                       />
                                    </div>
                                 ))}
                              </div>
                           </div>
                        </>
                     )}
                  </div>
               )}

               {message && (
                  <div
                     className={cn(
                        "text-sm p-3 rounded-lg text-center font-medium mb-3",
                        message.type === "success"
                           ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                           : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                     )}
                  >
                     {message.text}
                  </div>
               )}

               <DialogFooter className="flex justify-end pt-3 sm:pt-3">
                  <Button
                     onClick={handleSave}
                     disabled={isSaving || isLoading || settings.length === 0}
                     size="lg"
                     className="w-full rounded-3xl h-12 text-base font-semibold transition-all hover:scale-[1.01] active:scale-[0.99]"
                  >
                     {isSaving ? <Loading size="sm" /> : translate("saveButton")}
                  </Button>
               </DialogFooter>
               {isFirstTime && (
                  <div className="text-xs text-muted-foreground bg-muted/50 rounded-full leading-normal text-center">
                     {translate("footer")}
                  </div>
               )}
            </div>
         </DialogContent>
      </Dialog>
   );
}
